import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Eraser, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SignatureModal = ({ open, onOpenChange, quoteId, token, onSignatureComplete }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isClosingRef = useRef(false); // Prevent re-open during close

  // Initialize canvas
  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setHasDrawn(false);
    }
  }, [open]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSubmit = useCallback(async () => {
    // Prevent double-submit
    if (submitting || isClosingRef.current) {
      console.log('[SIGNATURE] Blocked: already submitting or closing');
      return;
    }
    
    if (!hasDrawn) {
      toast.error('Veuillez dessiner une signature');
      return;
    }
    
    // Validate quoteId before proceeding
    if (!quoteId) {
      console.error('[SIGNATURE] ERROR: quoteId is undefined!');
      toast.error('Erreur: ID du devis manquant');
      return;
    }

    setSubmitting(true);
    
    // Build URL
    const targetUrl = `${API}/api/quotes/${quoteId}/sign-immediate`;
    console.log('[SIGNATURE] Submitting to:', targetUrl);
    console.log('[SIGNATURE] quoteId:', quoteId);
    console.log('[SIGNATURE] token present:', !!token);
    
    try {
      // Get canvas as base64 (PNG)
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas not found');
      }
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      console.log('[SIGNATURE] Base64 length:', base64?.length);

      console.log('[SIGNATURE] Sending POST...');
      const response = await axios.post(
        targetUrl,
        { signature_image_base64: base64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[SIGNATURE] Response:', response.status);

      if (response.status === 200) {
        // Mark as closing to prevent re-open
        isClosingRef.current = true;
        
        // Show success toast
        toast.success('Devis signé avec succès !');
        
        // CRITICAL: Close modal FIRST, synchronously
        onOpenChange(false);
        
        // Small delay then refresh
        setTimeout(() => {
          onSignatureComplete?.();
          // Reset closing flag after everything is done
          setTimeout(() => {
            isClosingRef.current = false;
          }, 500);
        }, 200);
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
      
    } catch (error) {
      console.error('[SIGNATURE] Error:', error);
      console.error('[SIGNATURE] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
      setSubmitting(false);
    }
  }, [hasDrawn, quoteId, token, submitting, onOpenChange, onSignatureComplete]);

  // Safe handler for dialog open/close - prevents re-open during closing
  const handleOpenChange = useCallback((newOpen) => {
    if (isClosingRef.current && newOpen) {
      console.log('[SIGNATURE] Blocked re-open during closing');
      return;
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Signature du client</DialogTitle>
          <DialogDescription>
            Le client signe ci-dessous pour accepter ce devis.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Canvas */}
          <div className="border rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
          </div>

          {/* Clear button */}
          <Button
            type="button"
            variant="outline"
            onClick={clearCanvas}
            className="w-full"
            data-testid="clear-signature-btn"
          >
            <Eraser className="w-4 h-4 mr-2" />
            Effacer
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            En signant, le client accepte les termes de ce devis.
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!hasDrawn || submitting}
            className="bg-green-600 hover:bg-green-700"
            data-testid="submit-signature-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signature...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Valider la signature
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureModal;
